class SeoExpand extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;

    this.contentEl = this.querySelector('.seo-expand__content');
    this.btnEl = this.querySelector('.seo-expand__btn');
    this.showTextEl = this.querySelector('.seo-expand__show-text');
    this.hideTextEl = this.querySelector('.seo-expand__hide-text');

    if (!this.contentEl || !this.btnEl) return;

    this.heightDesktop = parseInt(this.dataset.heightDesktop) || 260;
    this.heightMobile = parseInt(this.dataset.heightMobile) || 180;

    this.style.setProperty('--seo-height-desktop', this.heightDesktop + 'px');
    this.style.setProperty('--seo-height-mobile', this.heightMobile + 'px');

    this._busy = false;

    // Wait for full page load (fonts + images) before measuring height
    if (document.readyState === 'complete') {
      this._init();
    } else {
      window.addEventListener('load', () => this._init(), { once: true });
    }
  }

  disconnectedCallback() {
    this._ro?.disconnect();
  }

  _threshold() {
    return window.matchMedia('(min-width: 750px)').matches
      ? this.heightDesktop
      : this.heightMobile;
  }

  _init() {
    if (this.contentEl.scrollHeight <= this._threshold()) return;

    // Suppress transition during initial instant collapse
    this.contentEl.style.transition = 'none';
    this.classList.add('is-collapsible', 'is-collapsed');
    // Restore transition after browser paints the collapsed state
    requestAnimationFrame(() => {
      this.contentEl.style.transition = '';
    });

    this._setLabels(false);
    this.btnEl.addEventListener('click', () => this._toggle());

    this._ro = new ResizeObserver(() => {
      if (this._busy) return;
      if (this.contentEl.scrollHeight <= this._threshold()) {
        this.classList.remove('is-collapsible', 'is-collapsed');
        this.btnEl.hidden = true;
      }
    });
    this._ro.observe(this.contentEl);
  }

  _setLabels(isOpen) {
    this.btnEl.setAttribute('aria-expanded', String(isOpen));
    if (this.showTextEl) this.showTextEl.hidden = isOpen;
    if (this.hideTextEl) this.hideTextEl.hidden = !isOpen;
  }

  _toggle() {
    if (this._busy) return;
    this.classList.contains('is-collapsed') ? this._open() : this._close();
  }

  _open() {
    this._busy = true;
    const threshold = this._threshold();
    const fullHeight = this.contentEl.scrollHeight;

    // Pin at threshold (same as CSS), remove class constraint, then animate up
    this.contentEl.style.transition = 'none';
    this.contentEl.style.maxHeight = threshold + 'px';
    this.classList.remove('is-collapsed');

    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.contentEl.style.transition = '';
      this.contentEl.style.maxHeight = fullHeight + 'px';

      this.contentEl.addEventListener('transitionend', () => {
        this.contentEl.style.maxHeight = ''; // free for dynamic content
        this._busy = false;
      }, { once: true });
    }));

    this._setLabels(true);
  }

  _close() {
    this._busy = true;
    const fullHeight = this.contentEl.scrollHeight;
    const threshold = this._threshold();

    // Pin at full height, then animate down to threshold
    this.contentEl.style.transition = 'none';
    this.contentEl.style.maxHeight = fullHeight + 'px';

    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.contentEl.style.transition = '';
      this.contentEl.style.maxHeight = threshold + 'px';

      this.contentEl.addEventListener('transitionend', () => {
        // Add class BEFORE clearing inline style so CSS takes over seamlessly
        this.classList.add('is-collapsed');
        this.contentEl.style.maxHeight = '';
        this._busy = false;
      }, { once: true });
    }));

    this._setLabels(false);
  }
}

customElements.define('seo-expand', SeoExpand);
